import { common } from '@/features/i18n/common';
import { t } from '@/features/i18n/utils';
import { tryCatch } from '@/try-catch';
import { AnalysisStatus } from '@/features/workspaces/constants';
import { ProcurementBreadcrumbs } from '@/features/subcontractor-bids/components/procurement-breadcrumbs';
import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useCreateBlockNote } from '@blocknote/react';
import { Icon } from '@/ui/components/icon';
import { Download01Icon, FileEditIcon, RefreshIcon } from '@hugeicons-pro/core-stroke-rounded';
import { BreadcrumbPage } from '@/ui/components/breadcrumb';
import { Header, HeaderBackButton, HeaderChatButton } from '@/ui/components/header';
import {
  MainHeader,
  MainHeaderBackButton,
  MainHeaderGroup,
  MainHeaderGroupItem,
  MainScrollArea,
} from '@/ui/components/main';
import { H1 } from '@/ui/components/heading';
import { Button } from '@/ui/components/button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyMedia,
  EmptyTitle,
} from '@/ui/components/empty';
import { toastManager } from '@/ui/components/toast';
import { exportBlocksToDocx } from '@/ui/components/editor/export-to-docx';
import { procurementDetailsQuery, subRfpQuery } from '@/features/subcontractor-bids/queries';
import { useRunSubRfp } from '@/features/subcontractor-bids/mutations';
import { useProcurementAnalysisReady } from '@/features/subcontractor-bids/helpers';
import { Tooltip, TooltipPopup, TooltipTrigger } from '@/ui/components/tooltip';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogPopup,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/ui/components/alert-dialog';
import { Markdown } from '@/ui/components/markdown';
import { GroupSeparator } from '@/ui/components/group';
import { Progress, ProgressTrack, ProgressValue } from '@/ui/components/progress';

export const Route = createFileRoute(
  '/_authed/project/$projectId/workspaces/$workspaceId/subcontractor-bids/$procurementId/scope-of-work',
)({
  component: RouteComponent,
  loader: ({ context, params }) =>
    context.queryClient.query({ ...subRfpQuery(params.procurementId), staleTime: 'static' }),
});

function RouteComponent() {
  const { projectId, workspaceId, procurementId } = Route.useParams();
  const [rerunOpen, setRerunOpen] = useState(false);
  const { data: procurement } = useSuspenseQuery(
    procurementDetailsQuery(workspaceId, procurementId),
  );
  const { data: subRfp } = useSuspenseQuery(subRfpQuery(procurementId));
  const { isReadyToAnalyse, notReadyTooltip } = useProcurementAnalysisReady();

  const editor = useCreateBlockNote();

  const rerun = useRunSubRfp();

  const isDone = subRfp?.status === AnalysisStatus.DONE && !!subRfp.contentMd;
  const isProcessing = subRfp?.status === AnalysisStatus.IN_PROGRESS;

  return (
    <>
      <Header>
        <HeaderBackButton />
        <ProcurementBreadcrumbs>
          <BreadcrumbPage>
            <Icon icon={FileEditIcon} />
            {t({ en: 'Draft scope of work', nb: 'Lag UE-forespørselseunderlag' })}
          </BreadcrumbPage>
        </ProcurementBreadcrumbs>
        <HeaderChatButton />
      </Header>
      <MainScrollArea>
        <MainHeader className="border-b z-5 relative">
          <H1>
            <MainHeaderBackButton
              to="/project/$projectId/workspaces/$workspaceId/subcontractor-bids/$procurementId"
              params={{ projectId, workspaceId, procurementId }}
            />
            {t({ en: 'Draft scope of work', nb: 'Lag UE-forespørselseunderlag' })}
          </H1>
          {isDone && (
            <Tooltip>
              <MainHeaderGroup>
                <AlertDialog open={rerunOpen} onOpenChange={setRerunOpen}>
                  <MainHeaderGroupItem
                    render={
                      <AlertDialogTrigger
                        render={
                          <Button
                            render={<TooltipTrigger delay={0} />}
                            variant="secondary"
                            kind="with-icon"
                            disabled={!isReadyToAnalyse || rerun.isPending}
                          >
                            <Icon icon={RefreshIcon} />
                            {t({ en: 'Regenerate', nb: 'Regenerer' })}
                          </Button>
                        }
                      />
                    }
                  />
                  <AlertDialogPopup>
                    <AlertDialogTitle>{t({ en: 'Regenerate', nb: 'Regenerer' })}?</AlertDialogTitle>
                    <AlertDialogDescription>
                      {t(common.reRunAnalysisDescription)}
                    </AlertDialogDescription>
                    <AlertDialogFooter>
                      <AlertDialogCancel disabled={rerun.isPending} />
                      <Button
                        pending={rerun.isPending}
                        onClick={() =>
                          rerun.mutate(undefined, { onSuccess: () => setRerunOpen(false) })
                        }
                      >
                        {t({ en: 'Regenerate', nb: 'Regenerer' })}
                      </Button>
                    </AlertDialogFooter>
                  </AlertDialogPopup>
                </AlertDialog>
                <GroupSeparator />
                <MainHeaderGroupItem
                  render={
                    <Button
                      variant="secondary"
                      kind="with-icon"
                      onClick={async () => {
                        if (!subRfp?.contentMd) return;
                        const { error } = await tryCatch(() =>
                          exportBlocksToDocx(
                            editor.tryParseMarkdownToBlocks(subRfp.contentMd),
                            procurement.displayName,
                          ),
                        );
                        if (!error) return;
                        console.error('[ExportRfpDocx]', error);
                        toastManager.add({
                          type: 'error',
                          title: t({ en: 'Something went wrong', nb: 'Noe gikk galt' }),
                        });
                      }}
                    >
                      <Icon icon={Download01Icon} />
                      {t(common.download)}
                    </Button>
                  }
                />
              </MainHeaderGroup>
              {!isReadyToAnalyse && <TooltipPopup>{notReadyTooltip}</TooltipPopup>}
            </Tooltip>
          )}
        </MainHeader>
        <div className="px-x py-6">
          {isDone ? (
            <Markdown className="max-w-200" text={subRfp.contentMd} />
          ) : (
            <Empty>
              <EmptyMedia variant="icon">
                <Icon icon={FileEditIcon} />
              </EmptyMedia>
              <EmptyContent>
                <EmptyTitle>
                  {t({ en: 'Draft scope of work', nb: 'Lag UE-forespørselseunderlag' })}
                </EmptyTitle>
                <EmptyDescription>
                  {t({
                    en: 'Generate a scope of work for selected disciplines in this pack',
                    nb: 'Opprett en UE-forespørselseunderlag for valgte fagområden i pakken',
                  })}
                </EmptyDescription>
                {isProcessing && (
                  <Progress
                    value={subRfp?.progress ?? null}
                    max={100}
                    className="flex items-center gap-2 max-w-62"
                  >
                    <ProgressTrack />
                    <ProgressValue className="text-muted text-sm shrink-0" />
                  </Progress>
                )}
              </EmptyContent>
            </Empty>
          )}
        </div>
      </MainScrollArea>
    </>
  );
}
